import { CheckCircle2, Loader2, Calendar } from 'lucide-react';
import solidLogo from '@/assets/solid-logo-darkbg.svg';

interface QualificationResult {
  qualified: boolean;
  qualificationScore: number;
  dealId?: string | null;
  bookingUrl: string;
}

interface ConfirmationStepProps {
  result: QualificationResult | null;
  isLoading?: boolean;
}

export function ConfirmationStep({
  result,
  isLoading = false,
}: ConfirmationStepProps) {
  const qualified = result?.qualified ?? false;
  const bookingUrl = result?.bookingUrl;

  // Loading state: AI review in progress
  if (isLoading) {
    return (
      <div className="flex flex-col items-center text-center py-16">
        <Loader2 className="h-10 w-10 text-accent-500 animate-spin mb-6" />
        <p className="text-lg font-semibold text-white mb-1">
          Reviewing your project...
        </p>
        <p className="text-sm text-slate-400 max-w-sm">
          Our AI is analyzing your submission. This usually takes just a moment.
        </p>
      </div>
    );
  }

  // Qualified state
  if (qualified) {
    return (
      <div className="flex flex-col items-center text-center py-10">
        <img src={solidLogo} alt="Solid Digital" className="h-7 mb-8 opacity-60" />

        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20 mb-5">
          <CheckCircle2 className="h-6 w-6 text-green-400" />
        </div>

        <h3 className="text-lg font-semibold text-white mb-2">
          We're excited about your project!
        </h3>

        <p className="text-sm text-slate-400 max-w-md mb-8 leading-relaxed">
          Our team will review your submission and reach out within 1 business day.
          In the meantime, feel free to book an intro call.
        </p>

        <div className="w-full max-w-sm space-y-3">
          {bookingUrl && (
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full rounded-lg bg-accent-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-700"
            >
              <Calendar className="h-4 w-4" />
              Book an Intro Call
            </a>
          )}

          {/* Create account link — hidden for now */}
        </div>

        <p className="mt-8 text-xs text-slate-600">
          &copy; 2026 Solid Digital
        </p>
      </div>
    );
  }

  // Not qualified state
  return (
    <div className="flex flex-col items-center text-center py-10">
      <img src={solidLogo} alt="Solid Digital" className="h-7 mb-8 opacity-60" />

      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20 mb-5">
        <CheckCircle2 className="h-6 w-6 text-green-400" />
      </div>

      <h3 className="text-lg font-semibold text-white mb-2">
        Thanks for your interest!
      </h3>

      <p className="text-sm text-slate-400 max-w-md mb-8 leading-relaxed">
        We've received your inquiry and our team will follow up shortly.
      </p>

      <p className="mt-8 text-xs text-slate-600">
        &copy; 2026 Solid Digital
      </p>
    </div>
  );
}
